import {fetchJSON} from '../../platform/functional-fetch-wrapper'

const SUGGESTIONS_URL = '/_ghui/item-pickers/labels/suggestions'

export type GetLabelSuggestionsRequest = {
  owner: string
  repo: string
}

/**
 * A single suggestion entry, mirroring `serialize_suggested_label` in
 * `Ghui::ItemPickers::SuggestedLabels`. `fullDatabaseId` is serialized as a
 * string (`label.id.to_s`) and is the only field memex joins on — memex keys
 * labels by their numeric id, while `id` here is a global relay id.
 *
 * The serializer also returns the label's `archivedAt`, which is deliberately
 * not modelled here. Suggestions only ever *decorate* memex's own label list,
 * and rendering reads those objects, so a suggested label already carries the
 * archived state (and archived treatment) it would have in any other group.
 * Mirroring `archivedAt` here would add a second, independently-fetched source
 * of truth for the same fact, with no reader and a real chance of disagreeing.
 */
type LabelSuggestionEntry = {
  label: {
    id: string
    fullDatabaseId: string
    name: string
    nameHTML: string
    color: string
    description?: string | null
    url: string
  }
  reason: string
  rank: number
}

/**
 * The endpoint answers with `errors` instead of `suggestions` for a repository
 * the viewer cannot read, and with an empty list when either server flag gating
 * it is off. Both are non-exceptional: the picker renders without a suggestions
 * group. Neither is reported as a failure — unlike a rejected request, which is
 * (see `fetchSuggestionAttributions`).
 */
type GetLabelSuggestionsResponse = {
  suggestions?: Array<LabelSuggestionEntry | null> | null
  errors?: Array<{message: string}>
}

/**
 * Attribution for a suggested label, keyed by the numeric label id so callers
 * can join against memex's own label list.
 */
export type LabelSuggestionAttribution = {
  labelId: number
  reason: string
  /**
   * The server's rank *within its reason* — it restarts at 0 for each reason,
   * so it identifies a label's standing among its peers, not its position in
   * the merged list. Reporting only; never sort by this.
   */
  rank: number
  /**
   * Position in the server's merged, precedence-ordered list. This is the only
   * field that describes the order the picker should render.
   */
  order: number
}

function toAttributions(entries: GetLabelSuggestionsResponse['suggestions']): Array<LabelSuggestionAttribution> {
  const attributions: Array<LabelSuggestionAttribution> = []

  for (const entry of entries ?? []) {
    const labelId = Number(entry?.label?.fullDatabaseId)
    if (!entry || !Number.isFinite(labelId)) continue

    attributions.push({labelId, reason: entry.reason, rank: entry.rank, order: attributions.length})
  }

  return attributions
}

/**
 * Fetches the server-ranked label suggestions for a repository. The response is
 * already merged, deduplicated, ranked and capped server-side, so the returned
 * order is authoritative and must be preserved by callers.
 */
export async function apiGetLabelSuggestions({
  owner,
  repo,
}: GetLabelSuggestionsRequest): Promise<Array<LabelSuggestionAttribution>> {
  const url = new URL(SUGGESTIONS_URL, window.location.origin)
  url.searchParams.set('owner', owner)
  url.searchParams.set('repo', repo)

  const {data} = await fetchJSON<GetLabelSuggestionsResponse>(url)

  if (data.errors?.length) return []

  return toAttributions(data.suggestions)
}
